import { useRef, useState } from 'react';
import { OBSPropertyInfo } from '../../types/obs';
import { FormControlLabel, Switch, TextField } from '@streamelements/frontend-ui';
import { SelectSimple } from '../Select';

export function ObsPropertyEditor({property, value, suffix, disabled, onChange} : {
    property: OBSPropertyInfo,
    value: unknown,
    suffix?: string,
    disabled?: boolean,
    onChange?: (value: unknown) => void,
}) {
    const [ error ] = useState(null);
    const ref = useRef<HTMLInputElement>(null);

    function currentValue(): unknown {
        if (property.dataType === 'string') {
            if (value) {
              return value;
            }

            if (property.controlType === 'select') {
              return property.items.find(item => item.value.length > 0)?.value ?? '';
            }

            return '';
        }

        if (property.dataType === 'bool') {
            return value ?? false;
        }

        return String(value || '0');
    }

    switch (property.controlType) {
        case 'text':
        case 'number':
          return (<>
            <TextField.Single
              disabled={disabled}
              label={property.label}
              value={(value ?? (property.controlType === 'number' ? '0' :'')) as string}
              onChange={(e) => {
                let value =
                  property.controlType === 'number' ? e.target.valueAsNumber : e.target.value;

                if (property.controlType === 'number') {
                  value = isNaN(value as number) ? '0' : value;
                }

                onChange?.(value);
              }}
              inputProps={{ ref: ref }}
              type={property.controlType === 'number' ? 'number' : 'text'}
              error={!!error}
            >
              {suffix ? <TextField.Adornment>{suffix}</TextField.Adornment> : null}
              {error ? (
                <TextField.HelperText>{error}</TextField.HelperText>
              ) : null}
            </TextField.Single>
          </>);
        case 'checkbox':
          return (<>
            <FormControlLabel css={{ my: '0.5rem' }}>
              <Switch
                disabled={disabled}
                checked={!!currentValue()}
                onCheckedChange={(checked) => {
                    onChange?.(checked);
                }}
              />
              {property.label}
            </FormControlLabel>
          </>);
        case 'select':
          return (<>
            <SelectSimple
              disabled={disabled}
              label={property.label}
              options={new Map(property.items.filter(item => item.value.length > 0).map((item) => { return [String(item.value).toLowerCase(), item.name]; }))}
              value={String(currentValue()).toLowerCase()}
              onChange={onChange}
            />
          </>);
        default:
          return <></>;
      }
}

export function ObsPropertiesEditor({properties, settings = {}, defaultSettings = {}, suffixes: suffixes = {}, disabled, onChange} : {
    properties: OBSPropertyInfo[],
    settings: Record<string, unknown>,
    defaultSettings: Record<string, unknown>,
    suffixes: Record<string, string>,
    disabled?: boolean,
    onChange?: (value: Record<string, unknown>) => void,
}) {
    const data: Record<string, unknown> = {};

    for (const key in defaultSettings) {
        data[key] = defaultSettings[key];
    }

    for (const key in settings) {
        data[key] = settings[key];
    }

    return (<>
        {properties.map((property) =>
            <ObsPropertyEditor
                disabled={disabled}
                key={property.name}
                property={property}
                value={data[property.name] ?? ''}
                suffix={suffixes[property.name] ?? ''}
                onChange={(value) => {
                    data[property.name] = value;

                    onChange?.(data);
                }}
            />
        )}
    </>);   
}
